import useCheckSignOff from '../../hooks/useCheckSignOff';
import React, { useState } from 'react';
import { FiCamera, FiCheckSquare, FiSquare, FiShield } from 'react-icons/fi';

export default function AllergenSignOff() {
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [hasSignature, setHasSignature] = useState(true);
  const signOff = useCheckSignOff({ slug: 'allergen-check', approved: confirmed, fields: { openingNotes: notes } });

  return (
    <div className="w-full bg-white rounded-2xl p-6 border border-gray-100 shadow-sm font-sans select-none">
      {/* Block Title */}
      <h2 className="text-gray-900 font-bold text-lg mb-6 text-left">
        Allergen Review Notes & Sign-off
      </h2>

      {/* Main Grid Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        
        {/* Left Column: Notes & Signature */}
        <div className="space-y-4">
          <div>
            <label className="block text-gray-900 font-bold text-sm mb-2 text-left">
              Add any notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about today's allergen review..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-gray-50/30"
            />
          </div>

          <div>
            <label className="block text-gray-900 font-bold text-sm mb-1 text-left">
              Manager signature
            </label>
            <div className="flex items-end justify-between min-h-[56px] py-1 border-b border-gray-200">
              {hasSignature ? (
                /* Simulated Handwritten Signature */
                <span className="text-2xl text-gray-900 font-medium tracking-wide pl-2 select-none font-serif italic" style={{ fontFamily: "'Caveat', 'Georgia', cursive" }}>
                  Arslan Nasir
                </span>
              ) : (
                <span className="text-sm text-gray-300 italic pl-2">Sign here...</span>
              )}
              
              <button 
                onClick={() => setHasSignature(!hasSignature)}
                className="text-xs font-bold text-blue-600 hover:text-blue-700 transition"
              >
                {hasSignature ? 'Clear' : 'Sign'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Photos & Confirmation Checkbox */}
        <div className="space-y-5">
          <div>
            <label className="block text-gray-900 font-bold text-sm mb-2 text-left">
              Add photos (optional)
            </label>
            <button className="w-full flex items-center justify-center space-x-2 py-3 border border-dashed border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition text-sm font-bold text-blue-600 shadow-sm">
              <FiCamera className="w-4 h-4" />
              <span>Upload photos</span>
            </button>
          </div>

          <div className="pt-2">
            <label className="block text-gray-900 font-bold text-sm mb-3 text-left">
              Confirm review completed
            </label>
            <div 
              onClick={() => setConfirmed(!confirmed)}
              className="flex items-start space-x-3 cursor-pointer group"
            >
              <div className="flex-shrink-0 mt-0.5">
                {confirmed ? (
                  <FiCheckSquare className="w-5 h-5 text-blue-600" />
                ) : (
                  <FiSquare className="w-5 h-5 text-gray-300 group-hover:text-gray-400 transition" />
                )}
              </div>
              <p className="text-xs text-gray-700 font-medium leading-relaxed text-left">
                I confirm all allergen management checks have been reviewed and updated.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Main Submission Call to Action Button */}
      <button type="button" disabled={!signOff.canSubmit} onClick={signOff.submit} className="disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center space-x-2 py-3.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-100 transition duration-150 mb-4">
        <FiCheckSquare className="w-4 h-4" />
        <span>Approve & Complete Allergen Review Check</span>
      </button>

      {/* Bottom Security Audit Notice Line */}
      <div className="flex items-center space-x-2 text-xs text-blue-800/80 font-medium">
        <FiShield className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <p className="text-left">
          All checks are timestamped and stored securely for compliance and audit purposes.
        </p>
      </div>
    </div>
  );
}
